import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Step1JobType } from './FormSteps/Step1JobType';
import { Step2ConditionalQuestions } from './FormSteps/Step2ConditionalQuestions';
import { Step3ProjectInfo } from './FormSteps/Step3ProjectInfo';
import { Step4ContactDetails } from './FormSteps/Step4ContactDetails';
import { Step5Summary } from './FormSteps/Step5Summary';
import { ProgressBar } from './UI/ProgressBar';
import { Button } from './UI/Button';
import { CheckIcon, LoaderIcon, ArrowRightIcon, ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export const MultiStepForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [expandedStep, setExpandedStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Job Type
    jobTypes: {
      schilderwerk: false,
      stucwerk: false,
      badkamerRenovatie: false,
      toiletRenovatie: false,
      aanbouw: false,
      completeWoningrenovatie: false,
      anders: false,
      andersText: ''
    },
    // Step 2: Conditional questions
    schilderwerk: {
      leeftijdWoning: '',
      specificaties: {
        muren: false,
        plafonds: false,
        deuren: false,
        kozijnen: false
      },
      murenM2: '',
      plafondsM2: '',
      deurenAantal: '',
      kozijnenAantal: ''
    },
    stucwerk: {
      leeftijdWoning: '',
      specificaties: {
        muren: false,
        plafonds: false
      },
      murenM2: '',
      plafondsM2: ''
    },
    toiletRenovatie: {
      elektrischeVloerverwarming: false,
      nisInMuur: false,
      verlaagdPlafond: false,
      inbouwWc: false,
      oppervlakte: ''
    },
    badkamerRenovatie: {
      elektrischeVloerverwarming: false,
      nisInDouche: false,
      verlaagdPlafond: false,
      toiletinstallatie: false,
      oppervlakte: ''
    },
    completeWoningrenovatie: {
      keukeninstallatie: false,
      isolatie: false,
      vloerverwarming: false,
      badkamerToiletRenovatie: false,
      elektraInstallatie: false,
      herinrichtenRuimtes: false,
      totaleOppervlakte: ''
    },
    aanbouw: {
      aanbouwType: ''
    },
    // Step 3: Project Info
    projectInfo: {
      gewensteUitvoerdatum: '',
      projectOmschrijving: '',
      linkNaarFotos: ''
    },
    // Step 4: Contact Details
    contactDetails: {
      naam: '',
      emailadres: '',
      telefoonnummer: '',
      straat: '',
      huisnummer: '',
      toevoeging: '',
      postcode: '',
      plaats: ''
    }
  });
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const totalSteps = 5;
  // Add tactile feedback for mobile
  const provideTactileFeedback = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(40);
    }
  };
  const handleStepToggle = (step: number) => {
    provideTactileFeedback();
    // If we're clicking on the current expanded step, don't do anything
    if (step === expandedStep) return;
    // If we're trying to expand a step ahead of what's allowed, don't do anything
    if (step > 1 && !completedSteps.includes(step - 1)) return;
    setExpandedStep(step);
    setCurrentStep(step);
  };
  const handleNext = () => {
    if (validateCurrentStep()) {
      provideTactileFeedback();
      // Mark current step as completed
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      const nextStep = currentStep + 1;
      setAnimateOut(true);
      setTimeout(() => {
        setCurrentStep(nextStep);
        setExpandedStep(nextStep);
        setAnimateOut(false);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 300);
    }
  };
  const handlePrevious = () => {
    provideTactileFeedback();
    const prevStep = currentStep - 1;
    setAnimateOut(true);
    setTimeout(() => {
      setCurrentStep(prevStep);
      setExpandedStep(prevStep);
      setAnimateOut(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 300);
  };
  const validateCurrentStep = () => {
    let isValid = true;
    const newErrors: Record<string, any> = {};
    if (currentStep === 1) {
      // Validate at least one job type is selected
      const anySelected = Object.values(formData.jobTypes).some(value => value === true && typeof value === 'boolean');
      if (!anySelected) {
        newErrors.jobTypes = 'Selecteer ten minste één type opdracht';
        isValid = false;
      }
      // Validate "Anders" text if "Anders" is selected
      if (formData.jobTypes.anders && !formData.jobTypes.andersText.trim()) {
        newErrors.andersText = 'Vul in wat voor type opdracht';
        isValid = false;
      }
    } else if (currentStep === 4) {
      // Validate contact details
      const {
        naam,
        emailadres,
        telefoonnummer,
        straat,
        huisnummer,
        postcode,
        plaats
      } = formData.contactDetails;
      if (!naam.trim()) {
        newErrors.naam = 'Naam is verplicht';
        isValid = false;
      }
      if (!emailadres.trim()) {
        newErrors.emailadres = 'E-mailadres is verplicht';
        isValid = false;
      } else if (!/\S+@\S+\.\S+/.test(emailadres)) {
        newErrors.emailadres = 'Voer een geldig e-mailadres in';
        isValid = false;
      }
      if (!telefoonnummer.trim()) {
        newErrors.telefoonnummer = 'Telefoonnummer is verplicht';
        isValid = false;
      }
      if (!straat?.trim()) {
        newErrors.straat = 'Straat is verplicht';
        isValid = false;
      }
      if (!huisnummer?.trim()) {
        newErrors.huisnummer = 'Huisnummer is verplicht';
        isValid = false;
      }
      if (!postcode?.trim()) {
        newErrors.postcode = 'Postcode is verplicht';
        isValid = false;
      } else if (!/^\d{4}\s?[A-Za-z]{2}$/.test(postcode.replace(/\s/g, ''))) {
        newErrors.postcode = 'Voer een geldige postcode in (1234 AB)';
        isValid = false;
      }
      if (!plaats?.trim()) {
        newErrors.plaats = 'Plaats is verplicht';
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };
  const [userIp, setUserIp] = useState('');
  // Fetch user IP on component mount
  useEffect(() => {
    const fetchUserIp = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIp(data.ip);
      } catch (error) {
        console.error('Failed to fetch IP address:', error);
        setUserIp('Unknown');
      }
    };
    fetchUserIp();
  }, []);
  // Generate a formatted subject line for the email
  const generateEmailSubject = (data: typeof formData) => {
    const name = data.contactDetails.naam || 'Onbekende klant';
    // Get selected job types
    const jobTypes = Object.entries(data.jobTypes).filter(([key, value]) => value === true && key !== 'anders').map(([key]) => {
      switch (key) {
        case 'schilderwerk':
          return 'Schilderwerk';
        case 'stucwerk':
          return 'Stucwerk';
        case 'badkamerRenovatie':
          return 'Badkamer renovatie';
        case 'toiletRenovatie':
          return 'Toilet renovatie';
        case 'aanbouw':
          return 'Aanbouw';
        case 'completeWoningrenovatie':
          return 'Complete woningrenovatie';
        default:
          return key;
      }
    });
    if (data.jobTypes.anders) {
      jobTypes.push(data.jobTypes.andersText);
    }
    const jobTypesText = jobTypes.join(', ');
    return `Nieuwe offerte aanvraag van ${name} - ${jobTypesText}`;
  };
  // Generate HTML email body
  const generateEmailHtml = (data: typeof formData) => {
    const {
      jobTypes,
      schilderwerk,
      stucwerk,
      toiletRenovatie,
      badkamerRenovatie,
      completeWoningrenovatie,
      aanbouw,
      projectInfo,
      contactDetails
    } = data;
    // Format date for display
    const currentDate = new Date().toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    // Format time for display
    const currentTime = new Date().toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
    // Get selected job types
    const selectedJobTypes = Object.entries(jobTypes).filter(([key, value]) => value === true && key !== 'anders').map(([key]) => {
      switch (key) {
        case 'schilderwerk':
          return 'Schilderwerk';
        case 'stucwerk':
          return 'Stucwerk';
        case 'badkamerRenovatie':
          return 'Badkamer renovatie';
        case 'toiletRenovatie':
          return 'Toilet renovatie';
        case 'aanbouw':
          return 'Aanbouw';
        case 'completeWoningrenovatie':
          return 'Complete woningrenovatie';
        default:
          return key;
      }
    });
    if (jobTypes.anders) {
      selectedJobTypes.push(`Anders: ${jobTypes.andersText}`);
    }
    // Generate HTML sections for each job type if selected
    let jobDetailsHtml = '';
    // Schilderwerk section
    if (jobTypes.schilderwerk) {
      const specificaties: string[] = [];
      if (schilderwerk.specificaties.muren) specificaties.push(`Muren (${schilderwerk.murenM2 || 0} m²)`);
      if (schilderwerk.specificaties.plafonds) specificaties.push(`Plafonds (${schilderwerk.plafondsM2 || 0} m²)`);
      if (schilderwerk.specificaties.deuren) specificaties.push(`Deuren (${schilderwerk.deurenAantal || 0} stuks)`);
      if (schilderwerk.specificaties.kozijnen) specificaties.push(`Kozijnen (${schilderwerk.kozijnenAantal || 0} stuks)`);
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Schilderwerk</h3>
            ${schilderwerk.leeftijdWoning ? `<p style="margin: 8px 0 0 0;">Leeftijd woning: ${schilderwerk.leeftijdWoning}</p>` : ''}
            ${specificaties.length > 0 ? `<p style="margin: 8px 0 0 0;">Specificaties: ${specificaties.join(', ')}</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Stucwerk section
    if (jobTypes.stucwerk) {
      const specificaties: string[] = [];
      if (stucwerk.specificaties.muren) specificaties.push(`Muren (${stucwerk.murenM2 || 0} m²)`);
      if (stucwerk.specificaties.plafonds) specificaties.push(`Plafonds (${stucwerk.plafondsM2 || 0} m²)`);
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Stucwerk</h3>
            ${stucwerk.leeftijdWoning ? `<p style="margin: 8px 0 0 0;">Leeftijd woning: ${stucwerk.leeftijdWoning}</p>` : ''}
            ${specificaties.length > 0 ? `<p style="margin: 8px 0 0 0;">Specificaties: ${specificaties.join(', ')}</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Toilet renovatie section
    if (jobTypes.toiletRenovatie) {
      const wensen: string[] = [];
      if (toiletRenovatie.elektrischeVloerverwarming) wensen.push('Elektrische vloerverwarming');
      if (toiletRenovatie.nisInMuur) wensen.push('Nis in muur');
      if (toiletRenovatie.verlaagdPlafond) wensen.push('Verlaagd plafond');
      if (toiletRenovatie.inbouwWc) wensen.push('Inbouw wc');
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Toilet renovatie</h3>
            ${wensen.length > 0 ? `<p style="margin: 8px 0 0 0;">Extra wensen: ${wensen.join(', ')}</p>` : ''}
            ${toiletRenovatie.oppervlakte ? `<p style="margin: 8px 0 0 0;">Oppervlakte: ${toiletRenovatie.oppervlakte} m²</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Badkamer renovatie section
    if (jobTypes.badkamerRenovatie) {
      const wensen: string[] = [];
      if (badkamerRenovatie.elektrischeVloerverwarming) wensen.push('Elektrische vloerverwarming');
      if (badkamerRenovatie.nisInDouche) wensen.push('Nis in douche');
      if (badkamerRenovatie.verlaagdPlafond) wensen.push('Verlaagd plafond');
      if (badkamerRenovatie.toiletinstallatie) wensen.push('Toiletinstallatie');
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Badkamer renovatie</h3>
            ${wensen.length > 0 ? `<p style="margin: 8px 0 0 0;">Extra wensen: ${wensen.join(', ')}</p>` : ''}
            ${badkamerRenovatie.oppervlakte ? `<p style="margin: 8px 0 0 0;">Oppervlakte: ${badkamerRenovatie.oppervlakte} m²</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Complete woningrenovatie section
    if (jobTypes.completeWoningrenovatie) {
      const wensen: string[] = [];
      if (completeWoningrenovatie.keukeninstallatie) wensen.push('Keukeninstallatie');
      if (completeWoningrenovatie.isolatie) wensen.push('Isolatie');
      if (completeWoningrenovatie.vloerverwarming) wensen.push('Vloerverwarming');
      if (completeWoningrenovatie.badkamerToiletRenovatie) wensen.push('Badkamer/toilet renovatie');
      if (completeWoningrenovatie.elektraInstallatie) wensen.push('Gehele nieuwe elektra installatie');
      if (completeWoningrenovatie.herinrichtenRuimtes) wensen.push('Herinrichten van ruimtes');
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Complete woningrenovatie</h3>
            ${wensen.length > 0 ? `<p style="margin: 8px 0 0 0;">Extra wensen: ${wensen.join(', ')}</p>` : ''}
            ${completeWoningrenovatie.totaleOppervlakte ? `<p style="margin: 8px 0 0 0;">Totale oppervlakte: ${completeWoningrenovatie.totaleOppervlakte} m²</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Aanbouw section
    if (jobTypes.aanbouw) {
      let aanbouwTypeText = '';
      switch (aanbouw.aanbouwType) {
        case 'casco':
          aanbouwTypeText = 'Casco (Wordt kaal opgeleverd zonder enige afwerking)';
          break;
        case 'allin':
          aanbouwTypeText = 'All-in (Aflevering inclusief stuccen, schilderen en standaard elektriciteit)';
          break;
        case 'allin-extra':
          aanbouwTypeText = "All-in met extra's (Zoals: vloerverwarming, meer stopcontacten, overstek, buitenkraan)";
          break;
        default:
          aanbouwTypeText = aanbouw.aanbouwType || 'Niet gespecificeerd';
      }
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Aanbouw</h3>
            <p style="margin: 8px 0 0 0;">Aanbouwtype: ${aanbouwTypeText}</p>
          </td>
        </tr>
      `;
    }
    // Anders section
    if (jobTypes.anders && jobTypes.andersText) {
      jobDetailsHtml += `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Anders</h3>
            <p style="margin: 8px 0 0 0;">${jobTypes.andersText}</p>
          </td>
        </tr>
      `;
    }
    // Project info section
    let projectInfoHtml = '';
    if (projectInfo.gewensteUitvoerdatum || projectInfo.projectOmschrijving || projectInfo.linkNaarFotos) {
      projectInfoHtml = `
        <tr>
          <td style="padding: 16px; border-bottom: 1px solid #e2e8f0;">
            <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #111010;">Projectinformatie</h3>
            ${projectInfo.gewensteUitvoerdatum ? `<p style="margin: 8px 0 0 0;"><strong>Gewenste uitvoerdatum:</strong> ${projectInfo.gewensteUitvoerdatum}</p>` : ''}
            ${projectInfo.projectOmschrijving ? `
              <p style="margin: 8px 0 0 0;"><strong>Projectomschrijving:</strong></p>
              <p style="margin: 4px 0 0 0; padding-left: 12px;">${projectInfo.projectOmschrijving}</p>
            ` : ''}
            ${projectInfo.linkNaarFotos ? `<p style="margin: 8px 0 0 0;"><strong>Link naar foto's:</strong> ${projectInfo.linkNaarFotos}</p>` : ''}
          </td>
        </tr>
      `;
    }
    // Define CSS styles
    const emailStyles = `
      body {
        font-family: "Montserrat", Arial, sans-serif;
        line-height: 1.6;
        color: #4a5568;
        margin: 0;
        padding: 0;
        background-color: #f7fafc;
      }
      .email-container {
        max-width: 600px;
        margin: 0 auto;
        background-color: #ffffff;
        border-radius: 8px;
        overflow: hidden;
      }
      .email-header {
        background-color: #111010;
        padding: 20px;
        text-align: center;
      }
      .email-body {
        padding: 20px;
      }
      .email-footer {
        background-color: #f8fafc;
        padding: 20px;
        text-align: center;
        font-size: 12px;
        color: #718096;
        border-top: 1px solid #e2e8f0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      .highlight {
        background-color: #FEECCF;
        padding: 12px;
        border-radius: 6px;
        margin-bottom: 20px;
      }
      .highlight p {
        margin: 0;
        color: #111010;
        font-weight: 500;
      }
      .section-title {
        color: #111010;
        font-size: 18px;
        font-weight: 600;
        margin-top: 0;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #FCCA86;
      }
      .contact-info {
        background-color: #f8fafc;
        border-radius: 6px;
        padding: 16px;
        margin-bottom: 24px;
      }
      .meta-info {
        font-size: 11px;
        color: #a0aec0;
        margin-top: 12px;
      }
    `;
    // Full HTML email template (FIXED: proper `${emailStyles}` injection)
    const emailHtml = `
  <!DOCTYPE html>
  <html lang="nl">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Offerte Aanvraag</title>
    <style>${emailStyles}</style>
  </head>
  <body>
    <div class="email-container">
      <div class="email-header">
        <img src="https://btbbouwbedrijf.nl/wp-content/uploads/2020/05/Logo-Transparency-WHITE.png" alt="BTB Bouwbedrijf Logo" style="max-height: 60px;">
      </div>
      <div class="email-body">
        <div class="highlight">
          <p>Nieuwe offerte aanvraag ontvangen op ${currentDate} om ${currentTime}</p>
        </div>
        <h2 class="section-title">Contactgegevens</h2>
        <div class="contact-info">
          <p><strong>Naam:</strong> ${contactDetails.naam || 'Niet opgegeven'}</p>
          <p><strong>E-mail:</strong> ${contactDetails.emailadres || 'Niet opgegeven'}</p>
          <p><strong>Telefoon:</strong> ${contactDetails.telefoonnummer || 'Niet opgegeven'}</p>
          <p><strong>Adres:</strong> ${contactDetails.straat || ''} ${contactDetails.huisnummer || ''}${contactDetails.toevoeging ? `-${contactDetails.toevoeging}` : ''}, ${contactDetails.postcode || ''} ${contactDetails.plaats || ''}</p>
        </div>
        <h2 class="section-title">Type opdracht</h2>
        <p>${selectedJobTypes.join(', ')}</p>
        <h2 class="section-title">Specificaties</h2>
        <table>
          ${jobDetailsHtml}
        </table>
        ${projectInfoHtml ? `
          <h2 class="section-title">Projectinformatie</h2>
          <table>
            ${projectInfoHtml}
          </table>
        ` : ''}
        <div class="meta-info">
          <p>IP-adres aanvrager: ${userIp}</p>
          <p>Aanvraag via: https://www.btbbouwbedrijf.nl</p>
        </div>
      </div>
      <div class="email-footer">
        <p>&copy; ${new Date().getFullYear()} BTB Bouwbedrijf. Alle rechten voorbehouden.</p>
        <p>Website door <a href="https://www.raduxdigital.nl" style="color: #718096; text-decoration: none;">Radux Digital Agency</a></p>
      </div>
    </div>
  </body>
  </html>
    `;
    return emailHtml;
  };
  const handleSubmit = () => {
    if (validateCurrentStep()) {
      provideTactileFeedback();
      setIsSubmitting(true);
      // Generate email HTML and subject
      const emailHtml = generateEmailHtml(formData);
      const emailSubject = generateEmailSubject(formData);
      // Prepare enhanced data for the webhook
      const enhancedData = {
        ...formData,
        meta: {
          timestamp: new Date().toISOString(),
          ipAddress: userIp,
          source: 'https://www.btbbouwbedrijf.nl',
          userAgent: navigator.userAgent
        },
        email: {
          subject: emailSubject,
          htmlBody: emailHtml
        }
      };
      // Clear any previous conversion tracking flags before redirecting
      if (window.__btbConvInFlight) {
        window.__btbConvInFlight = false;
      }
      sessionStorage.removeItem('btb_conversion_tracked_v1');
      // Send data to webhook
      fetch('https://hook.eu2.make.com/4nezd0ros69tgs53mgs8kisofdaaidb6', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enhancedData)
      }).then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      }).then(data => {
        console.log('Verzonden!', data);
        setIsSubmitting(false);
        // Redirect to success page - let the SuccessPage handle the tracking
        navigate('/bedankt');
      }).catch(error => {
        console.error('Fout bij verzenden van formulier:', error);
        // Still redirect to success page even if the webhook fails
        setIsSubmitting(false);
        navigate('/bedankt');
      });
    }
  };
  const handleInputChange = (section: string, field: string, value: any) => {
    if (field.includes('.')) {
      // Handle nested fields like 'specificaties.muren'
      const [parent, child] = field.split('.');
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parent]: {
            ...prev[section][parent],
            [child]: value
          }
        }
      }));
      // Mark field as touched
      setTouched(prev => ({
        ...prev,
        [`${section}.${field}`]: true
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
      // Mark field as touched
      setTouched(prev => ({
        ...prev,
        [`${section}.${field}`]: true
      }));
    }
  };
  // Get step title
  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return 'Type opdracht';
      case 2:
        return 'Specificaties';
      case 3:
        return 'Project info';
      case 4:
        return 'Contact';
      case 5:
        return 'Samenvatting';
      default:
        return '';
    }
  };
  // Get step description
  const getStepDescription = (step: number) => {
    switch (step) {
      case 1:
        return 'Selecteer één of meerdere types opdrachten waarvoor u een offerte wilt ontvangen.';
      case 2:
        return 'Vul de details in voor de gekozen opdrachten.';
      case 3:
        return 'Vul de algemene informatie in over uw project.';
      case 4:
        return 'Vul uw contactgegevens in zodat we u kunnen bereiken over uw offerte.';
      case 5:
        return 'Controleer uw gegevens voordat u de offerte aanvraagt.';
      default:
        return '';
    }
  };
  // Get step summary for collapsed state
  const getStepSummary = (step: number) => {
    if (!completedSteps.includes(step)) return null;
    switch (step) {
      case 1:
        const selectedJobs = Object.entries(formData.jobTypes).filter(([key, value]) => value === true && key !== 'anders').map(([key]) => {
          switch (key) {
            case 'schilderwerk':
              return 'Schilderwerk';
            case 'stucwerk':
              return 'Stucwerk';
            case 'badkamerRenovatie':
              return 'Badkamer renovatie';
            case 'toiletRenovatie':
              return 'Toilet renovatie';
            case 'aanbouw':
              return 'Aanbouw';
            case 'completeWoningrenovatie':
              return 'Complete woningrenovatie';
            default:
              return key;
          }
        });
        if (formData.jobTypes.anders) {
          selectedJobs.push(`Anders: ${formData.jobTypes.andersText}`);
        }
        return selectedJobs.join(', ');
      case 2:
        return 'Specificaties ingevuld';
      case 3:
        return formData.projectInfo.gewensteUitvoerdatum ? `Uitvoerdatum: ${formData.projectInfo.gewensteUitvoerdatum}` : 'Project info ingevuld';
      case 4:
        return formData.contactDetails.naam ? `${formData.contactDetails.naam} - ${formData.contactDetails.emailadres}` : 'Contact ingevuld';
      default:
        return null;
    }
  };
  // Render step content
  const renderStepContent = (step: number) => {
    switch (step) {
      case 1:
        return <Step1JobType formData={formData} handleInputChange={handleInputChange} errors={errors} touched={touched} setTouched={setTouched} data-id="element-397" />;
      case 2:
        return <Step2ConditionalQuestions formData={formData} handleInputChange={handleInputChange} errors={errors} touched={touched} setTouched={setTouched} data-id="element-398" />;
      case 3:
        return <Step3ProjectInfo formData={formData} handleInputChange={handleInputChange} errors={errors} touched={touched} setTouched={setTouched} data-id="element-399" />;
      case 4:
        return <Step4ContactDetails formData={formData} handleInputChange={handleInputChange} errors={errors} touched={touched} setTouched={setTouched} data-id="element-400" />;
      case 5:
        return <Step5Summary formData={formData} isSubmitting={isSubmitting} isSubmitted={false} data-id="element-401" />;
      default:
        return null;
    }
  };
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only if not in an input, textarea, or select
      if (!['INPUT', 'TEXTAREA', 'SELECT'].includes((document.activeElement as HTMLElement)?.tagName || '')) {
        if (e.key === 'ArrowRight' && currentStep < totalSteps) {
          handleNext();
        } else if (e.key === 'ArrowLeft' && currentStep > 1) {
          handlePrevious();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);
  return <div className="bg-white shadow-md rounded-lg transition-all duration-300 overflow-hidden" data-id="element-402">
      <div className="p-4 md:p-5" data-id="element-403">
        {/* Only show progress bar on mobile */}
        <div className="sm:hidden" data-id="element-404">
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} completedSteps={completedSteps} data-id="element-405" />
        </div>
        <div className="mt-4 transition-all duration-300" data-id="element-406">
          {/* Accordion-style steps */}
          <div className="space-y-2.5" data-id="element-407">
            {Array.from({
            length: totalSteps
          }, (_, i) => i + 1).map(step => {
            const isExpanded = expandedStep === step;
            const isCompleted = completedSteps.includes(step);
            const isAccessible = step === 1 || completedSteps.includes(step - 1);
            const stepSummary = getStepSummary(step);
            return <div key={step} className={`rounded-md transition-all duration-200 ${isExpanded ? 'ring-1 ring-[#FCCA86] shadow-sm' : isCompleted ? 'border border-gray-200 hover:border-[#FCCA86]' : 'border border-gray-200'} ${isAccessible ? 'bg-white' : 'bg-gray-50'}`} data-id="element-408">
                  <button type="button" onClick={() => isAccessible && handleStepToggle(step)} className={`w-full flex items-center justify-between p-3 text-left ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`} disabled={!isAccessible} data-id="element-409">
                    <div className="flex items-center" data-id="element-410">
                      <div className={`flex items-center justify-center w-6 h-6 mr-2.5 rounded-full ${isCompleted ? 'bg-[#FCCA86] text-white' : isExpanded ? 'bg-[#111010] text-white' : 'bg-gray-100 text-gray-500'} transition-colors duration-300`} data-id="element-411">
                        {isCompleted ? <CheckIcon className="w-3.5 h-3.5" data-id="element-412" /> : <span className="text-xs font-medium" data-id="element-413">{step}</span>}
                      </div>
                      <div data-id="element-414">
                        <span className="font-medium text-[14px] block" data-id="element-415">
                          {getStepTitle(step)}
                        </span>
                        {!isExpanded && <span className="text-xs text-gray-500 hidden md:block" data-id="element-416">
                            {getStepDescription(step)}
                          </span>}
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-417">
                      {!isExpanded && stepSummary && <span className="text-xs text-gray-600 mr-3 hidden sm:block max-w-xs truncate" data-id="element-418">
                          {stepSummary}
                        </span>}
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors duration-200 ${isExpanded ? 'bg-gray-100' : 'bg-white'}`} data-id="element-419">
                        {isExpanded ? <ChevronUpIcon className="w-4 h-4 text-gray-700" data-id="element-420" /> : <ChevronDownIcon className="w-4 h-4 text-gray-700" data-id="element-421" />}
                      </div>
                    </div>
                  </button>
                  {isExpanded && <div className={`px-3 pb-4 pt-0 ${animateOut ? 'animate-fadeOut' : 'animate-fadeIn'}`} data-id="element-422">
                      <div className="pt-1" data-id="element-423">{renderStepContent(step)}</div>
                      <div className="mt-5 flex flex-col-reverse sm:flex-row justify-between gap-2" data-id="element-424">
                        {step > 1 && <Button variant="outline" onClick={handlePrevious} className="w-full sm:w-auto" data-id="element-425">
                            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-426" />
                            Vorige
                          </Button>}
                        {step < totalSteps ? <Button variant={step === 1 ? 'secondary' : 'primary'} onClick={handleNext} className="w-full sm:w-auto" data-id="element-427">
                            Volgende
                            <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-428" />
                          </Button> : <Button variant="secondary" onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto" data-id="element-429">
                            {isSubmitting ? <>
                                <LoaderIcon className="mr-2 h-4 w-4 animate-spin" data-id="element-430" />
                                Versturen...
                              </> : 'Offerte aanvragen'}
                          </Button>}
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
};